import { SearchMd } from "@untitledui/icons";
import { useState } from "react";
import { useAdminMerchants } from "@/api/admin/merchants";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";

type Props = {
  value: string[];
  onChange: (ids: string[]) => void;
  /** IDs of merchants already in the workspace — hidden from the picker */
  excludeMerchantIds?: string[];
};

export const WorkspaceBrandsPicker = ({ value, onChange, excludeMerchantIds = [] }: Props) => {
  const [search, setSearch] = useState("");

  const { data: merchantsData } = useAdminMerchants({ offset: 0, limit: 500 }, { column: "id", direction: "asc" }, {});

  const selectedSet = new Set(value);

  const toggleBrand = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const filteredMerchants = (merchantsData?.merchants ?? []).filter(
    (m) => !excludeMerchantIds.includes(m.id) && m.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="flex flex-col flex-1 overflow-hidden gap-3">
      <h2 className="text-xl font-semibold text-primary shrink-0">Select merchants to add to workspace</h2>

      <div className="shrink-0">
        <Input
          placeholder="Search"
          value={search}
          onChange={setSearch}
          icon={SearchMd}
          wrapperClassName="bg-primary ring-primary rounded-xl"
          iconClassName="text-fg-quaternary"
          inputClassName="text-primary placeholder:text-placeholder"
        />
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-2">
        {filteredMerchants.length === 0 && <p className="text-center text-sm text-tertiary py-6">No merchants found</p>}

        {filteredMerchants.map((merchant) => {
          const isSelected = selectedSet.has(merchant.id);
          return (
            <button
              key={merchant.id}
              type="button"
              onClick={() => toggleBrand(merchant.id)}
              className={`w-full shrink-0 flex items-center overflow-hidden rounded-xl border transition-colors hover:bg-white/5 ${
                isSelected ? "border-brand-600 bg-white/5" : "border-primary"
              }`}
            >
              <MerchantLogo
                logoUrl={merchant.logoUrl}
                name={merchant.name}
                size="sm"
                className="!w-14 !h-14 !rounded-none !ring-0 flex-shrink-0"
              />
              <span className="flex-1 text-sm font-medium text-primary text-left px-3">{merchant.name}</span>
              {merchant.region && (
                <span className="mr-3 flex-shrink-0 rounded-full border border-secondary px-2 py-0.5 text-xs font-medium text-tertiary">
                  {merchant.region}
                </span>
              )}
              <div className="pr-4 flex-shrink-0">
                <CheckboxBase size="md" isSelected={isSelected} />
              </div>
            </button>
          );
        })}

        <div className="shrink-0 flex flex-col gap-1 px-4 py-3 rounded-xl bg-secondary">
          <span className="text-sm font-medium text-primary">Merchant not here?</span>
          <span className="text-sm text-tertiary">
            Merchants need customer input before they appear here. If one's missing, don't worry, you can add more after
            setup.
          </span>
        </div>
      </div>
    </div>
  );
};
